import {
  buildCreatePayload,
  validatePhotos,
  type AddItemStep,
  type CreatedItem,
  type ItemCurrency,
} from "@kovernow/flows/add_item";
import { acceptFor, type UploadQueue } from "@kovernow/flows/uploads";
import { Button } from "@kovernow/react-ui/components/button";
import { UploadField } from "@kovernow/react-ui/components/upload-field";
import { useState } from "react";
import { GatedButton } from "../../components/GatedButton";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { STEP_HEADING_ID, StepHeading, type StepProps } from "./shared";
import { BusyLine, PipelineStatus, useUploadPipeline } from "./upload-pipeline";

/**
 * Step 5: photos (1–10) and an optional receipt (0–5). Nothing uploads until
 * "Add item to vault"; then the queue uploads 3 at a time, and `POST /items`
 * is sent only when every file is done. A failed file gets its own Retry;
 * a token/config failure or a failed POST shows a RecoveryView. Uploaded
 * files are kept, so a resubmit never uploads them again.
 */
export function PhotosStep({
  fields,
  back,
  headingRef,
  serverErrors,
  clearServerError,
  queue,
  ownerId,
  reattach,
  declarationShown,
  inFlight,
  onFieldError,
  onCreated,
}: Pick<StepProps, "fields" | "back" | "headingRef" | "serverErrors" | "clearServerError"> & {
  queue: UploadQueue;
  ownerId: string | null;
  reattach: string[];
  declarationShown: boolean;
  /** Owned by AddItem: browser Back/Forward remounts this step mid-save. */
  inFlight: { current: boolean };
  onFieldError: (step: AddItemStep, field: string, messageKey: string) => void;
  onCreated: (created: CreatedItem) => void;
}) {
  const t = useT();
  const { addItemApi, config } = useServices();
  const [attempted, setAttempted] = useState(false);
  const pipeline = useUploadPipeline({
    queue,
    ownerId,
    inFlight,
    createItem: async () => {
      const payload = buildCreatePayload(fields, {
        currency: config.currency as ItemCurrency,
        documents: queue.documents(),
        partnerDeclarationShown: declarationShown,
      });
      onCreated(await addItemApi.create(payload));
    },
    onFieldError: (target, messageKey) => onFieldError(target.step, target.field, messageKey),
  });
  const { qs, busy, locked, rowsFor, limitError, common } = pipeline;

  const photoCount = queue.count("photo");
  const submit = () => {
    setAttempted(true);
    if (validatePhotos(photoCount) || queue.preparing()) return;
    void pipeline.run(() => queue.uploadPending(ownerId));
  };

  const photoError =
    (serverErrors.photos ? t(serverErrors.photos) : undefined) ??
    limitError("photo") ??
    (attempted && validatePhotos(photoCount) ? t(validatePhotos(photoCount)!) : undefined);

  const chosen = new Set(qs.files.map((f) => f.name));
  const toReattach = reattach.filter((n) => !chosen.has(n));

  return (
    <section aria-labelledby={STEP_HEADING_ID} className="flex flex-col gap-6" data-testid="step-photos">
      <StepHeading headingRef={headingRef}>{t("addItem.photos.title")}</StepHeading>

      <ReattachList names={toReattach} />

      <UploadField
        {...common}
        testId="upload-photos"
        label={t("addItem.photos.photos")}
        limits={t("uploads.limits.photo")}
        accept={acceptFor("photo")}
        rows={rowsFor("photo")}
        error={photoError}
        onFiles={(files) => {
          clearServerError("photos");
          void queue.add("photo", files);
        }}
      />
      <UploadField
        {...common}
        testId="upload-receipt"
        label={t("addItem.photos.receipt")}
        limits={t("uploads.limits.receipt")}
        accept={acceptFor("receipt")}
        rows={rowsFor("receipt")}
        error={limitError("receipt")}
        onFiles={(files) => void queue.add("receipt", files)}
      />

      <PipelineStatus uploadsFailed={pipeline.uploadsFailed} recovery={pipeline.recovery} onRetry={submit} />

      <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-start">
        <div className="sm:min-w-56">
          <GatedButton
            variant="cta"
            label={t("addItem.submit")}
            busy={locked}
            unavailableReason={qs.files.some((f) => f.status === "preparing") ? t("common.loading") : undefined}
            onClick={submit}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="touch"
          aria-disabled={locked ? true : undefined}
          onClick={() => {
            // Leaving mid-upload would remount the step and allow a second POST.
            if (!locked) back();
          }}
        >
          {t("addItem.back")}
        </Button>
      </div>
      <BusyLine busy={busy} />
    </section>
  );
}

/** "Re-attach these files": names chosen before a reload (files are never drafted). */
export function ReattachList({ names }: { names: string[] }) {
  const t = useT();
  if (!names.length) return null;
  return (
    <div className="flex flex-col gap-1 rounded-lg border p-4" data-testid="reattach">
      <h3 className="font-semibold">{t("addItem.photos.reattach")}</h3>
      <p className="text-sm text-muted-foreground">{t("addItem.photos.reattachBody")}</p>
      <ul className="list-disc pl-5 text-sm">
        {names.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </div>
  );
}
